export const calculateCoverageLevel = (tags: string[] = []): number => {
  const coverageLevel = 1;

  const hasSpouse = tags.some((tag) => tag.startsWith('spouse'));
  const dependentCount = tags.filter((tag) =>
    tag.startsWith('dependent')
  ).length;

  if (hasSpouse && dependentCount > 0) {
    return 4; // Family
  }
  if (hasSpouse) {
    return 2; // Primary + Spouse
  }
  if (dependentCount > 0) {
    return 3; // Primary + Dependents
  }

  return coverageLevel; // Individual
};

type ApplicantRole = 'primary' | 'spouse' | 'dependent';

interface CoverageApplicantLike {
  applicant?: ApplicantRole | number | string;
}

export const getApplicantRole = (
  applicant?: CoverageApplicantLike['applicant']
): ApplicantRole | null => {
  if (applicant === 1 || applicant === '1' || applicant === 'primary') {
    return 'primary';
  }
  if (applicant === 2 || applicant === '2' || applicant === 'spouse') {
    return 'spouse';
  }
  if (applicant === 3 || applicant === '3' || applicant === 'dependent') {
    return 'dependent';
  }
  return null;
};

export const getCoverageTagForApplicantIndex = (
  applicants: CoverageApplicantLike[] = [],
  applicantIndex: number
): string | null => {
  const role = getApplicantRole(applicants[applicantIndex]?.applicant);
  if (role === 'spouse') return `spouse_${applicantIndex}`;
  if (role === 'dependent') return `dependent_${applicantIndex}`;
  return null;
};

export const getDefaultCoverageTags = (
  applicants: CoverageApplicantLike[] = []
): string[] =>
  applicants
    .map((_, index) => getCoverageTagForApplicantIndex(applicants, index))
    .filter((tag): tag is string => !!tag);

export const normalizeCoverageTags = (
  tags: string[] = [],
  applicants: CoverageApplicantLike[] = []
): string[] => {
  if (!Array.isArray(tags) || tags.length === 0) return [];

  const availableTags = getDefaultCoverageTags(applicants);
  const availableTagSet = new Set(availableTags);
  const spouseTag =
    availableTags.find((tag) => tag.startsWith('spouse')) ?? null;
  const dependentTags = applicants
    .map((_, index) => getCoverageTagForApplicantIndex(applicants, index))
    .filter((tag): tag is string => !!tag?.startsWith('dependent'));
  const normalizedTags: string[] = [];
  const addTag = (tag: string) => {
    if (!normalizedTags.includes(tag)) {
      normalizedTags.push(tag);
    }
  };

  tags.forEach((tag) => {
    if (availableTagSet.has(tag)) {
      addTag(tag);
    }
  });

  const wantsSpouse = tags.some((tag) => tag.startsWith('spouse'));
  if (
    wantsSpouse &&
    spouseTag &&
    !normalizedTags.some((tag) => tag.startsWith('spouse'))
  ) {
    addTag(spouseTag);
  }

  const selectedDependentCount = new Set(
    tags.filter((tag) => tag.startsWith('dependent'))
  ).size;
  const normalizedDependentCount = normalizedTags.filter((tag) =>
    tag.startsWith('dependent')
  ).length;
  const missingDependentCount = Math.max(
    0,
    selectedDependentCount - normalizedDependentCount
  );

  dependentTags
    .filter((tag) => !normalizedTags.includes(tag))
    .slice(0, missingDependentCount)
    .forEach(addTag);

  return normalizedTags;
};
